import { useEffect, useState } from "react";
import { adminCreateEvent, adminDeleteEvent, adminFetchEvents, adminUpdateEvent } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Edit2, Trash2, Plus, Lock } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", description: "", location: "", start_date: "", start_time: "", end_time: "", visibility: "public", image_url: "" };

export default function AdminEventsPage() {
    const [events, setEvents] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const list = await adminFetchEvents();
        setEvents(list);
    };
    useEffect(() => { load().catch(() => {}); }, []);

    const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
    const startEdit = (e) => {
        setEditing(e);
        setForm({
            title: e.title || "",
            description: e.description || "",
            location: e.location || "",
            start_date: e.start_date || "",
            start_time: e.start_time || "",
            end_time: e.end_time || "",
            visibility: e.visibility || "public",
            image_url: e.image_url || "",
        });
        setOpen(true);
    };

    const save = async () => {
        if (!form.title || !form.start_date) { toast.error("Title and start date are required."); return; }
        setSaving(true);
        try {
            if (editing) {
                const updated = await adminUpdateEvent(editing.id, form);
                setEvents((p) => p.map((x) => x.id === editing.id ? updated : x));
                toast.success("Event updated.");
            } else {
                const created = await adminCreateEvent(form);
                setEvents((p) => [created, ...p]);
                toast.success("Event added.");
            }
            setOpen(false);
        } catch (e) {
            toast.error("Save failed");
        } finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this event?")) return;
        await adminDeleteEvent(id);
        setEvents((p) => p.filter((x) => x.id !== id));
        toast.success("Deleted.");
    };

    return (
        <div data-testid="admin-events-manager">
            <div className="flex items-end justify-between gap-3 mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.32em] text-orange-300/90">Admin</div>
                    <h1 className="font-display text-3xl tracking-[0.05em] text-white">Events</h1>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <button className="ember-btn px-4 py-2 rounded-md text-sm font-display tracking-[0.12em] uppercase flex items-center gap-1" onClick={startCreate} data-testid="admin-events-add-button"><Plus size={14}/> Add Event</button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg bg-[#0a0a0b] border border-white/10 text-white" data-testid="admin-events-form-dialog">
                        <DialogHeader><DialogTitle>{editing ? "Edit Event" : "Add Event"}</DialogTitle></DialogHeader>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="sm:col-span-2">
                                <Label className="text-white/80">Title</Label>
                                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="admin-events-form-title" />
                            </div>
                            <div>
                                <Label className="text-white/80">Date</Label>
                                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="admin-events-form-date" />
                            </div>
                            <div>
                                <Label className="text-white/80">Visibility</Label>
                                <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                                    <SelectTrigger className="mt-1 bg-black/40 border-white/10" data-testid="admin-events-form-visibility"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="public">Public</SelectItem>
                                        <SelectItem value="private">Private</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-white/80">Start time</Label>
                                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="admin-events-form-start-time" />
                            </div>
                            <div>
                                <Label className="text-white/80">End time</Label>
                                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="admin-events-form-end-time" />
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="text-white/80">Location</Label>
                                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="admin-events-form-location" />
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="text-white/80">Description</Label>
                                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="admin-events-form-description" />
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="text-white/80">Image URL</Label>
                                <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="admin-events-form-image" />
                            </div>
                        </div>
                        <DialogFooter>
                            <button className="px-4 py-2 rounded-md border border-white/10 text-white/80" onClick={() => setOpen(false)} data-testid="admin-events-form-cancel">Cancel</button>
                            <button className="ember-btn px-4 py-2 rounded-md text-sm font-display tracking-[0.12em] uppercase" onClick={save} disabled={saving} data-testid="admin-events-form-save">{saving ? "Saving..." : "Save"}</button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <Card className="surface-card border-0">
                <CardContent className="p-0">
                    <div className="divide-y divide-white/10" data-testid="admin-events-list">
                        {events.length === 0 && <div className="p-6 text-white/65">No events.</div>}
                        {events.map((e) => (
                            <div key={e.id} className="flex items-start justify-between gap-4 p-4 sm:p-5" data-testid="admin-events-row">
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <div className="font-display text-lg tracking-[0.05em] text-white">{e.title}</div>
                                        {e.visibility === "private" ? (
                                            <Badge variant="outline" className="border-white/20 text-white/65"><Lock size={10} className="mr-1"/> Private</Badge>
                                        ) : (
                                            <Badge variant="outline" className="border-orange-400/40 text-orange-300 bg-orange-400/10">Public</Badge>
                                        )}
                                    </div>
                                    <div className="text-white/70 text-sm mt-1">
                                        {new Date(e.start_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                        {e.start_time ? ` · ${e.start_time}${e.end_time ? " – " + e.end_time : ""}` : ""}
                                    </div>
                                    {e.location && <div className="text-white/55 text-sm">{e.location}</div>}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button className="p-2 rounded-md border border-white/10 hover:bg-white/5" onClick={() => startEdit(e)} aria-label="Edit" data-testid="admin-events-edit-button"><Edit2 size={16}/></button>
                                    <button className="p-2 rounded-md border border-red-500/20 text-red-300 hover:bg-red-500/10" onClick={() => remove(e.id)} aria-label="Delete" data-testid="admin-events-delete-button"><Trash2 size={16}/></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
